import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buildLearnerNavigationState,
  getLearnerBackLabel,
  getLearnerNavigationState,
  navigateLearnerBack,
} from "src/pages/coaching/utils/manageNavigation";
import { useSelector } from "react-redux";
import { LearningRecordPodcast, UpdatePodcastSessionRequest } from "src/types/coaching/learners";
import React, { useEffect, useMemo } from "react";

import _ from 'lodash';
import cx from 'classnames';

import { useDispatch } from "react-redux";
import {
  fetchLearningRecordPodcast,
  fetchTranscriptsForLearningPodcast,
  startPodcast,
  updatePodcastSession
} from "src/redux/actions/coaching/learners";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { CourseNotFound } from "src/pages/coaching/components/notFound";

import styles from "./view.module.scss";
import { PodcastContext, PodcastContextProps } from "src/pages/coaching/components/podcast/context";
import { LoadingPodcastPage, PodcastSummaryComponent } from "src/pages/coaching/components/podcast";
import { PodcastTranscripts } from "src/types/coaching/podcast";
import { useDesktop } from "src/components/hooks/useResponsive";
import { hideSideMenu } from "src/redux/actions/hub";

export const ViewPodcastPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const learnerNavigation = useMemo(
    () => getLearnerNavigationState(location.state),
    [location.state],
  );

  const desktop = useDesktop();
  const [searchParams] = useSearchParams();

  const id = params.id;

  const podcast = useSelector((state: any) =>
    _.get(state.learners, `podcast[${id}].item`) as LearningRecordPodcast,
  );

  const loading = useSelector((state: any) =>
    _.get(state.learners, `podcast[${id}].loading`),
  );

  const podcastErrorCode = useSelector((state: any) => {
    return _.get(state.learners, `podcast[${id}].error.errors[0].code`);
  });

  const podcastTranscripts = useSelector((state: any) =>
    _.get(state.learners, `podcast[${id}].transcripts.item`) as PodcastTranscripts,
  );

  const [podcastContext, setPodcastContext] = React.useState<PodcastContextProps>({
    id: id,
  });

  const autoPlay = useMemo(() => {
    const autoPlayParam = searchParams.get("play");
    return autoPlayParam === "true";
  }, [searchParams]);

  useEffect(() => {
    if (id) {
      dispatch(fetchLearningRecordPodcast(id));
    }

    setPodcastContext((prev) => {
      return {
        ...prev,
        id: id
      }
    });
  }, []);

  useEffect(() => {
    if (podcast) {

      dispatch(fetchTranscriptsForLearningPodcast(podcast.id));

      setPodcastContext((prev) => {
        return {
          ...prev,
          startPodcast: handleStartPodcast,
          onAnalyticsChange: handleAnalyticsChange,
        }
      });
    }
  }, [podcast]);

  useEffect(() => {
    if (podcastTranscripts) {
      setPodcastContext((prev) => {
        return {
          ...prev,
          transcripts: podcastTranscripts,
        }
      });
    }
  }, [podcastTranscripts]);

  useEffect(() => {
    dispatch(hideSideMenu(!desktop));

    setPodcastContext(() => ({
      from: learnerNavigation.from,
      onBack: handleBackClicked,
    }));
  }, [desktop, learnerNavigation.from, learnerNavigation.mySpaceStatus]);

  const handleBackClicked = () => {
    navigateLearnerBack(navigate, learnerNavigation);
  };

  const handleStartPodcast = () => {
    if (podcast && podcast.id) {
      dispatch(startPodcast(podcast.id));
    }
  };

  const handleAnalyticsChange = (event: any) => {
    if (podcast && podcast.id && podcast.session && podcast.session.id) {
      const request: UpdatePodcastSessionRequest = {
        id: podcast.session!.id!,
        podcast: podcast.podcast!.id!,
        learningRecord: podcast.id,
        request: {
          dataPoints: event.analytics?.datapoints,
          sequence: new Date().getTime(),
          progress: event.analytics?.progress || 0,
          percentCompleted: event.analytics?.percentageRead || 0,
          duration: event.analytics?.duration || 0,
        },
      };

      dispatch(updatePodcastSession(request));
    }
  }

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (podcastErrorCode === 10007) {
    return <CourseNotFound onBack={handleBackClicked} />
  }

  if ((loading && podcast?.id != id) || !podcast) {
    return (
      <LoadingPodcastPage
        onBack={handleBackClicked}
        backLabel={getLearnerBackLabel(learnerNavigation.from)}
      />
    );
  }

  return (
    <PodcastContext.Provider value={podcastContext}>
      <PodcastSummaryComponent podcast={podcast} autoPlay={autoPlay} />
    </PodcastContext.Provider>
  );
}